import React from 'react';
import Divider from '@amzn/meridian/divider';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Box from '@amzn/meridian/box';
import Pie from '@amzn/meridian/pie';
import Legend, { LegendProvider } from '@amzn/meridian/legend';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Icon from '@amzn/meridian/icon';

import { useClient, useGet, useGetAll } from '$/api';
import { CorePage, CorePageTitle } from '$/components';
import { useIdParam } from '$/hooks';
import {
  LabHostsTable,
  LabDevicesTable,
  LabHostsWidget,
  LabEditButton /* HostsDevicesTable */,
} from './components';
import { LabsUri, RootUri } from '$/features/routes';

/* Bootstrap icons MIT license: https://github.com/twbs/icons/blob/main/LICENSE.md
The MIT License (MIT)
Copyright (c) 2019-2021 The Bootstrap Authors
Permission is hereby granted, free of charge, to any person obtaining a copy of
this software and associated documentation files (the Software), to deal in the
Software without restriction, including without limitation the rights to use,
copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
Software, and to permit persons to whom the Software is furnished to do so,
subject to the following conditions:

The above copyright notice and this permission notice shall be included in all 
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED AS IS, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

const labSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-house-fill" viewBox="0 0 16 16">
<path fill-rule="evenodd" d="m8 3.293 6 6V13.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5V9.293l6-6zm5-.793V6l-2-2V2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5z"/>
<path fill-rule="evenodd" d="M7.293 1.5a1 1 0 0 1 1.414 0l6.647 6.646a.5.5 0 0 1-.708.708L8 2.207 1.354 8.854a.5.5 0 1 1-.708-.708L7.293 1.5z"/>
</svg>`;
const hostSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pc" viewBox="0 0 16 16">
<path d="M5 0a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V1a1 1 0 0 0-1-1H5Zm.5 14a.5.5 0 1 1 0 1 .5.5 0 0 1 0-1Zm2 0a.5.5 0 1 1 0 1 .5.5 0 0 1 0-1ZM5 1.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5ZM5.5 3h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1 0-1Z"/>
</svg>`;
const deviceSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-usb-symbol" viewBox="0 0 16 16">
<path d="m7.792.312-1.533 2.3A.25.25 0 0 0 6.467 3H7.5v7.319a2.5 2.5 0 0 0-.515-.298L5.909 9.56A1.5 1.5 0 0 1 5 8.18v-.266a1.5 1.5 0 1 0-1 0v.266a2.5 2.5 0 0 0 1.515 2.298l1.076.461a1.5 1.5 0 0 1 .888 1.129 2.001 2.001 0 1 0 1.021-.006v-.902a1.5 1.5 0 0 1 .756-1.303l1.484-.848A2.5 2.5 0 0 0 11.995 7h.755a.25.25 0 0 0 .25-.25v-2.5a.25.25 0 0 0-.25-.25h-2.5a.25.25 0 0 0-.25.25v2.5c0 .138.112.25.25.25h.741a1.5 1.5 0 0 1-.747 1.142L8.76 8.99a2.584 2.584 0 0 0-.26.17V3h1.033a.25.25 0 0 0 .208-.389L8.208.312a.25.25 0 0 0-.416 0Z"/>
</svg>`;

/*
const locationIcon = {
  iconData: locationSvg,
  iconWidth: 24,
  iconHeight: 24,
}
*/
const labIcon = {
  iconData: labSvg,
  iconWidth: 24,
  iconHeight: 24,
};
const hostIcon = {
  iconData: hostSvg,
  iconWidth: 24,
  iconHeight: 24,
};
const deviceIcon = {
  iconData: deviceSvg,
  iconWidth: 24,
  iconHeight: 24,
};

export const LabDetailPage = (): JSX.Element => {
  const id = useIdParam();

  const { lmslabs, hosts, devices } = useClient();

  const {
    data: lmslab,
    error,
    isError,
    isLoading,
  } = useGet(lmslabs.resourceKey, lmslabs.get, id);

  const labId = lmslab?.id;

  const { data: dataLabs, isLoading: isLoadingLabs } = useGetAll(
    lmslabs.resourceKey,
    lmslabs.getAll,
    { labId }
  );
  const { data: dataHosts, isLoading: isLoadingHosts } = useGetAll(
    hosts.resourceKey,
    hosts.getAll,
    { labId }
  );
  const { data: dataDevices, isLoading: isLoadingDevices } = useGetAll(
    devices.resourceKey,
    devices.getAll,
    { labId }
  );

  const { data: valueBootloader } = useGet(
    devices.resourceKey,
    devices.getAll,
    { labId, page: 0, pageSize: 0, limit: 0, status: 'bootloader' }
  );
  /* 
  const { data: valueDeprecated } = useGet(
    devices.resourceKey,
    devices.getAll,
    { labId, page: 0, pageSize: 0, limit: 0, status: 'deprecated' }
  );
  */
  const { data: valueFatal } = useGet(devices.resourceKey, devices.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    status: 'fatal',
  });
  const { data: valueIdle } = useGet(devices.resourceKey, devices.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    status: 'idle',
  });
  const { data: valueMissing } = useGet(devices.resourceKey, devices.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    status: 'missing',
  });
  const { data: valueRunning } = useGet(devices.resourceKey, devices.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    status: 'running',
  });
  const { data: valueUnknown } = useGet(devices.resourceKey, devices.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    status: 'unknown',
  });
  const { data: valueDisabled } = useGet(devices.resourceKey, devices.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    status: 'disabled',
  });

  const valueBootloaderS = Number(valueBootloader?.total);
  // const valueDeprecatedS = Number(valueDeprecated?.total);
  const valueFatalS = Number(valueFatal?.total);
  const valueIdleS = Number(valueIdle?.total);
  const valueMissingS = Number(valueMissing?.total);
  const valueRunningS = Number(valueRunning?.total);
  const valueUnknownS = Number(valueUnknown?.total);
  const valueDisabledS = Number(valueDisabled?.total);

  const dsData = [
    { value: valueBootloaderS, key: 'bootloader', label: 'Bootloader' }, // api/v4/device?status=bootloader&page=0&pageSize=0&limit=0
    // { value: valueDeprecatedS, key: 'deprecated', label: 'Deprecated' },
    // api/v4/device?status=deprecated&page=0&pageSize=0&limit=0
    { value: valueFatalS, key: 'fatal', label: 'Fatal' }, // api/v4/device?status=fatal&page=0&pageSize=0&limit=0
    { value: valueIdleS, key: 'idle', label: 'Idle' }, // api/v4/device?status=idle&page=0&pageSize=0&limit=0
    { value: valueMissingS, key: 'missing', label: 'Missing' }, // api/v4/device?status=missing&page=0&pageSize=0&limit=0
    { value: valueRunningS, key: 'running', label: 'Running' }, // api/v4/device?status=running&page=0&pageSize=0&limit=0
    { value: valueUnknownS, key: 'unknown', label: 'Unknown' }, // api/v4/device?status=unknown&page=0&pageSize=0&limit=0
    { value: valueDisabledS, key: 'disabled', label: 'Disabled' }, // api/v4/device?status=unknown&page=0&pageSize=0&limit=0
  ];

  const { data: valueOffline } = useGet(hosts.resourceKey, hosts.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    status: 'offline',
  });
  const { data: valueOnline } = useGet(hosts.resourceKey, hosts.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    status: 'online',
  });
  const { data: valueUpdating } = useGet(hosts.resourceKey, hosts.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    status: 'updating',
  });
  const { data: valueQuarantined } = useGet(hosts.resourceKey, hosts.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    status: 'quarantined',
  });
  const { data: valuRegistered } = useGet(hosts.resourceKey, hosts.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    status: 'registered',
  });

  const valueOfflineS = Number(valueOffline?.total);
  const valueOnlineS = Number(valueOnline?.total);
  const valueUpdatingS = Number(valueUpdating?.total);
  const valuRegisteredS = Number(valuRegistered?.total);
  const valueQuarantinedS = Number(valueQuarantined?.total);

  const asData = [
    { value: valueOnlineS, key: 'Online', label: 'Online' }, // api/v4/host?status=online&page=0&pageSize=0&limit=0
    { value: valueOfflineS, key: 'Offline', label: 'Offline' }, // api/v4/host?status=offline&page=0&pageSize=0&limit=0
    { value: valuRegisteredS, key: 'Registered', label: 'Registered' }, // api/v4/host?status=registered&page=0&pageSize=0&limit=0
    { value: valueUpdatingS, key: 'Updating', label: 'Updating' }, // api/v4/host?status=updating&page=0&pageSize=0&limit=0
    { value: valueQuarantinedS, key: 'Quarantined', label: 'Quarantined' }, // api/v4/host?status=quarantined&page=0&pageSize=0&limit=0
  ];

  const { data: version707 } = useGet(hosts.resourceKey, hosts.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    version: '7.0.7',
  });
  const { data: version734 } = useGet(hosts.resourceKey, hosts.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    version: '7.3.4',
  });
  const { data: version736 } = useGet(hosts.resourceKey, hosts.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    version: '7.3.6',
  });
  const { data: version742 } = useGet(hosts.resourceKey, hosts.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    version: '7.4.2',
  });
  const { data: version7610 } = useGet(hosts.resourceKey, hosts.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    version: '7.6.10',
  });
  const { data: version793 } = useGet(hosts.resourceKey, hosts.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    version: '7.9.3',
  });
  const { data: version794 } = useGet(hosts.resourceKey, hosts.getAll, {
    labId,
    page: 0,
    pageSize: 0,
    limit: 0,
    version: '7.9.4',
  });

  const version707S = Number(version707?.total);
  const version734S = Number(version734?.total);
  const version736S = Number(version736?.total);
  const version742S = Number(version742?.total);
  const version7610S = Number(version7610?.total);
  const version793S = Number(version793?.total);
  const version794S = Number(version794?.total);

  /*
  7.3.4
  7.3.6
  7.4.2
  7.5.2
  7.5.4
  7.6.8
  7.6.10
  7.7.1
  7.9.3
  7.9.4
  */

  const avData = [
    { value: version707S, key: '7.0.7', label: '7.0.7' }, // api/v4/host?version=7.0.7&page=0&pageSize=0&limit=0
    { value: version734S, key: '7.3.4', label: '7.3.4' }, // api/v4/host?version=7.3.4&page=0&pageSize=0&limit=0
    { value: version736S, key: '7.3.6', label: '7.3.6' }, // api/v4/host?version=7.3.6&page=0&pageSize=0&limit=0
    { value: version742S, key: '7.4.2', label: '7.4.2' }, // api/v4/host?version=7.4.2&page=0&pageSize=0&limit=0
    { value: version7610S, key: '7.6.10', label: '7.6.10' }, // api/v4/host?version=7.6.10&page=0&pageSize=0&limit=0
    { value: version793S, key: '7.9.3', label: '7.9.3' }, // api/v4/host?version=7.9.3&page=0&pageSize=0&limit=0
    { value: version794S, key: '7.9.4', label: '7.9.4' }, // api/v4/host?version=7.9.4&page=0&pageSize=0&limit=0
  ];
  /*
    const hOSData = [
      { value: 26, key: "ubuntu18.04", label: "Ubuntu 18.04" },
      { value: 3, key: "ubuntu20.04", label: "Ubuntu 20.04" },
      { value: 1, key: "macOS12.6", label: "macOS 12.6" },
    ]
  */
  return (
    <CorePage
      title={lmslab?.name}
      isLoading={isLoading}
      isError={isError}
      error={error}
      breadcrumbs={[
        { title: 'LMS Lab Dashboard', href: RootUri },
        { title: 'Labs', href: LabsUri },
      ]}
      header={
        <>
          <Row widths={['fill', 'fit']}>
            <Column>
              {lmslab?.name && <CorePageTitle title={lmslab.name} />}
            </Column>
            {lmslab && (
              <Row alignmentHorizontal="right">
                <LabEditButton lmslab={lmslab} />
              </Row>
            )}
          </Row>
        </>
      }
    >
      <Column spacing="large">
        <Column spacingInset="medium" type="outline">
          <Heading level={3}>Summary</Heading>
          {lmslab?.description && (
            <>
              <Divider />
              <Text>{lmslab.description}</Text>
            </>
          )}

          {(!isLoadingLabs || !isLoadingHosts || !isLoadingDevices) && (
            <Table headerColumns={1} showDividers showStripes>
              {!lmslab?.id && (
                <TableRow>
                  <TableCell>
                    <Icon tokens={labIcon} />
                    Labs
                  </TableCell>
                  <TableCell>{dataLabs?.total}</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell>
                  <Icon tokens={hostIcon} />
                  Agents
                </TableCell>
                <TableCell>{dataHosts?.total}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Icon tokens={deviceIcon} />
                  Devices
                </TableCell>
                <TableCell>{dataDevices?.total}</TableCell>
              </TableRow>
            </Table>
          )}

          {lmslab?.id && <LabHostsWidget labId={lmslab.id} />}

          <Column spacingInset="small">
            <Row
              width="100%"
              widths="fill"
              spacing="medium"
              spacingInset="medium"
            >
              <Box
                type="outline"
                spacingInset="400"
                minHeight={325}
                minWidth={250}
              >
                <LegendProvider data={dsData} aria-labelledby="dStatus-one">
                  <Heading id="dStatus-one" level={4}>
                    Device Status
                  </Heading>
                  <Row
                    spacing="500"
                    alignmentHorizontal="center"
                    alignmentVertical="center"
                    minHeight={250}
                  >
                    <Pie width={200} data={dsData} donutRatio={0.7}>
                      <Text type="d100" tag="div">
                        {dsData.reduce(
                          (total, slice) => total + slice.value,
                          0
                        )}
                      </Text>
                      <Text type="h100" tag="div" color="secondary">
                        devices
                      </Text>
                    </Pie>
                    <Column>
                      <Legend
                        direction="vertical"
                        values={(item) => item.value.toLocaleString()}
                      />
                    </Column>
                  </Row>
                </LegendProvider>
              </Box>

              <Box
                type="outline"
                spacingInset="400"
                minHeight={325}
                minWidth={250}
              >
                <LegendProvider data={asData} aria-labelledby="dStatus-one">
                  <Heading id="dStatus-one" level={4}>
                    Agent Status
                  </Heading>
                  <Row
                    spacing="500"
                    alignmentHorizontal="center"
                    alignmentVertical="center"
                    minHeight={250}
                  >
                    <Pie width={200} data={asData} donutRatio={0.7}>
                      <Text type="d100" tag="div">
                        {asData.reduce(
                          (total, slice) => total + slice.value,
                          0
                        )}
                      </Text>
                      <Text type="h100" tag="div" color="secondary">
                        agents
                      </Text>
                    </Pie>
                    <Column>
                      <Legend
                        direction="vertical"
                        values={(item) => item.value.toLocaleString()}
                      />
                    </Column>
                  </Row>
                </LegendProvider>
              </Box>

              <Box
                type="outline"
                spacingInset="400"
                minHeight={325}
                minWidth={250}
              >
                <LegendProvider data={avData} aria-labelledby="dStatus-one">
                  <Heading id="dStatus-one" level={4}>
                    Agent Versions
                  </Heading>
                  <Row
                    spacing="500"
                    alignmentHorizontal="center"
                    alignmentVertical="center"
                    minHeight={250}
                  >
                    <Pie width={200} data={avData} donutRatio={0.7}>
                      <Text type="d100" tag="div">
                        {avData.reduce(
                          (total, slice) => total + slice.value,
                          0
                        )}
                      </Text>
                      <Text type="h100" tag="div" color="secondary">
                        agents
                      </Text>
                    </Pie>
                    <Column>
                      <Legend
                        direction="vertical"
                        values={(item) => item.value.toLocaleString()}
                      />
                    </Column>
                  </Row>
                </LegendProvider>
              </Box>
              {/* eslint-disable max-len */
              /*
              <Box type="outline" spacingInset="400" minHeight={100}>
                <LegendProvider data={hOSData} aria-labelledby="dStatus-one">
                  <Row spacing="500" alignmentHorizontal="center" alignmentVertical="top">
                    <Pie width={200} data={hOSData} donutRatio={0.7}>
                      <Text type="d100" tag="div">
                        {hOSData.reduce((total, slice) => total + slice.value, 0)}
                      </Text>
                      <Text type="h100" tag="div" color="secondary">
                        agents
                      </Text>
                    </Pie>
                    <Column>
                      <Heading id="dStatus-one" level={4}>
                        Host OS Versions
                      </Heading>
                      <Legend
                        direction="vertical"
                        values={item => item.value.toLocaleString()}
                      />
                    </Column>
                  </Row>
                </LegendProvider>
              </Box>
            */}
            </Row>
          </Column>
        </Column>
      </Column>

      <Column spacing="large">
        <Column spacingInset="medium" type="outline">
          {lmslab?.id && <LabHostsTable labId={lmslab.id} />}
        </Column>
      </Column>

      <Column spacing="large">
        <Column spacingInset="medium" type="outline">
          {lmslab?.id && <LabDevicesTable labId={lmslab.id} />}
        </Column>
      </Column>
    </CorePage>
  );
};
